import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import { API_CONFIG } from './config';
import { User } from '../types/auth';

const AUTH_TOKEN_KEY = '@laviola_auth_token';
const USER_DATA_KEY = '@laviola_user_data';

interface LoginResponse {
  success: boolean;
  user?: User;
  token?: string;
  message?: string;
}

// Create axios instance for auth
const authApi = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: 30000,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Error message translations
const errorTranslations: Record<string, string> = {
  'The email must be a valid email address':
    "Inserisci un'email valida o usa il tuo username.",
  'The email field is required': 'Inserisci email o username.',
  'The password field is required': 'Inserisci la password.',
  'Invalid credentials': 'Credenziali non valide.',
  'These credentials do not match our records': 'Credenziali non valide.',
  'Too many login attempts': 'Troppi tentativi di accesso. Riprova più tardi.',
  'Your IP has been blocked': 'Il tuo indirizzo IP è stato bloccato.',
  'Your account has been suspended': 'Il tuo account è stato sospeso.',
  'Your account is not verified': 'Il tuo account non è stato verificato.',
  'User not found': 'Utente non trovato.',
  'Account disabled': 'Account disabilitato.',
  'Account locked': 'Account bloccato.',
};

// Parse error message from API response
const parseErrorMessage = (error: any): string => {
  // Check for validation errors
  if (error.response?.data?.errors) {
    const errors = error.response.data.errors;
    // Get first error message
    const firstKey = Object.keys(errors)[0];
    const firstError = errors[firstKey]?.[0] || errors[firstKey];
    if (firstError) {
      return errorTranslations[firstError] || firstError;
    }
  }

  // Check for message in response
  if (error.response?.data?.message) {
    const msg = error.response.data.message;
    return errorTranslations[msg] || msg;
  }

  // Check for HTTP status codes
  if (error.response?.status === 403) {
    return 'Accesso negato. Il tuo IP potrebbe essere bloccato.';
  }
  if (error.response?.status === 429) {
    return 'Troppi tentativi. Riprova tra qualche minuto.';
  }
  if (error.response?.status === 401) {
    return 'Credenziali non valide.';
  }
  if (error.response?.status === 500) {
    return 'Errore del server. Riprova più tardi.';
  }

  // Network error
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'Connessione scaduta. Controlla la tua connessione.';
  }
  if (error.message?.includes('Network Error')) {
    return 'Errore di rete. Controlla la tua connessione.';
  }

  return 'Errore durante il login. Riprova.';
};

// Login user via API
export const login = async (
  email: string,
  password: string,
): Promise<LoginResponse> => {
  try {
    const response = await authApi.post('/api/app/login', {
      email,
      password,
    });

    if (response.data.success && response.data.user) {
      const { user, token } = response.data;

      // Store auth data
      if (token) {
        await AsyncStorage.setItem(AUTH_TOKEN_KEY, token);
      }
      await AsyncStorage.setItem(USER_DATA_KEY, JSON.stringify(user));

      return {
        success: true,
        user,
        token,
      };
    }

    // Handle API success:false response
    const message = response.data.message;
    return {
      success: false,
      message: errorTranslations[message] || message || 'Login fallito',
    };
  } catch (error: any) {
    console.error('Login error:', error);
    return {
      success: false,
      message: parseErrorMessage(error),
    };
  }
};

// Logout user
export const logout = async (): Promise<void> => {
  try {
    const token = await getStoredToken();

    if (token) {
      // Call logout API
      await authApi.post(
        '/api/app/logout',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
    }

    // Clear local storage
    await AsyncStorage.removeItem(AUTH_TOKEN_KEY);
    await AsyncStorage.removeItem(USER_DATA_KEY);
  } catch (error) {
    console.error('Logout error:', error);
    // Clear local storage anyway
    await AsyncStorage.removeItem(AUTH_TOKEN_KEY);
    await AsyncStorage.removeItem(USER_DATA_KEY);
  }
};

// Request account deletion — sends a verification code to the user's email
export const requestAccountDeletion = async (): Promise<{
  success: boolean;
  message?: string;
}> => {
  try {
    const token = await getStoredToken();

    if (!token) {
      return {success: false, message: 'Non sei autenticato.'};
    }

    const response = await authApi.post(
      '/api/app/account/request-deletion',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return {
      success: response.data.success,
      message: response.data.message,
    };
  } catch (error: any) {
    console.error('Request account deletion error:', error);
    return {
      success: false,
      message:
        error.response?.data?.message ||
        'Errore durante la richiesta. Riprova.',
    };
  }
};

// Delete user account permanently with verification code
export const deleteAccount = async (
  code: string,
): Promise<{success: boolean; message?: string}> => {
  try {
    const token = await getStoredToken();

    if (token) {
      const response = await authApi.delete('/api/app/account', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {code},
      });

      if (!response.data.success) {
        return {
          success: false,
          message:
            response.data.message ||
            "Errore durante l'eliminazione dell'account.",
        };
      }
    }

    // Clear local storage
    await AsyncStorage.removeItem(AUTH_TOKEN_KEY);
    await AsyncStorage.removeItem(USER_DATA_KEY);

    return {success: true};
  } catch (error: any) {
    console.error('Delete account error:', error);

    if (error.response?.status === 422) {
      return {
        success: false,
        message:
          error.response.data.message || 'Codice non valido o scaduto.',
      };
    }

    if (error.response?.status === 401) {
      await AsyncStorage.removeItem(AUTH_TOKEN_KEY);
      await AsyncStorage.removeItem(USER_DATA_KEY);
      return {success: true};
    }

    return {
      success: false,
      message: "Errore durante l'eliminazione dell'account. Riprova.",
    };
  }
};

// Get stored user
export const getStoredUser = async (): Promise<User | null> => {
  try {
    const userData = await AsyncStorage.getItem(USER_DATA_KEY);
    if (userData) {
      return JSON.parse(userData);
    }
    return null;
  } catch (error) {
    console.error('Get stored user error:', error);
    return null;
  }
};

// Get stored token
export const getStoredToken = async (): Promise<string | null> => {
  try {
    return await AsyncStorage.getItem(AUTH_TOKEN_KEY);
  } catch (error) {
    console.error('Get stored token error:', error);
    return null;
  }
};

// Verify token and get fresh user data
export const verifyToken = async (): Promise<User | null> => {
  try {
    const token = await getStoredToken();
    if (!token) return null;

    const response = await authApi.get('/api/app/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data.success && response.data.user) {
      // Update stored user data
      await AsyncStorage.setItem(
        USER_DATA_KEY,
        JSON.stringify(response.data.user),
      );
      return response.data.user;
    }

    // Token invalid, clear storage
    await AsyncStorage.removeItem(AUTH_TOKEN_KEY);
    await AsyncStorage.removeItem(USER_DATA_KEY);
    return null;
  } catch (error) {
    console.error('Verify token error:', error);
    return null;
  }
};

// Check if user is authenticated
export const isAuthenticated = async (): Promise<boolean> => {
  const token = await getStoredToken();
  const user = await getStoredUser();
  return !!token && !!user;
};

interface RegisterResponse {
  success: boolean;
  requires_verification?: boolean;
  user?: User;
  token?: string;
  message?: string;
  errors?: Record<string, string[]>;
}

// Register new user via API
export const register = async (
  firstName: string,
  lastName: string,
  email: string,
  password: string,
  passwordConfirmation: string,
): Promise<RegisterResponse> => {
  try {
    const response = await authApi.post('/api/app/register', {
      first_name: firstName,
      last_name: lastName,
      email,
      password,
      password_confirmation: passwordConfirmation,
    });

    if (response.data.success && response.data.user && response.data.token) {
      // Auto-login (no email verification needed)
      await AsyncStorage.setItem(AUTH_TOKEN_KEY, response.data.token);
      await AsyncStorage.setItem(
        USER_DATA_KEY,
        JSON.stringify(response.data.user),
      );
    }

    return response.data;
  } catch (error: any) {
    console.error('Register error:', error);

    if (error.response?.data?.message) {
      return {
        success: false,
        message: error.response.data.message,
        errors: error.response.data.errors,
      };
    }

    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        success: false,
        message: 'Connessione scaduta. Controlla la tua connessione.',
      };
    }
    if (error.message?.includes('Network Error')) {
      return {
        success: false,
        message: 'Errore di rete. Controlla la tua connessione.',
      };
    }

    return {
      success: false,
      message: 'Errore durante la registrazione. Riprova.',
    };
  }
};

interface ForgotPasswordResponse {
  success: boolean;
  message?: string;
}

// Request password reset email
export const forgotPassword = async (
  email: string,
): Promise<ForgotPasswordResponse> => {
  try {
    const response = await authApi.post('/api/app/forgot-password', { email });
    return response.data;
  } catch (error: any) {
    console.error('Forgot password error:', error);

    if (error.response?.data?.message) {
      return { success: false, message: error.response.data.message };
    }

    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      return {
        success: false,
        message: 'Connessione scaduta. Controlla la tua connessione.',
      };
    }
    if (error.message?.includes('Network Error')) {
      return {
        success: false,
        message: 'Errore di rete. Controlla la tua connessione.',
      };
    }

    return { success: false, message: 'Errore. Riprova.' };
  }
};
